import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  DeactivateUsersByOrganizationIdMutationRequest,
  DeactivateUsersByOrganizationIdMutationResponse,
} from "../types/DeactivateUsersByOrganizationId"

type DeactivateUsersByOrganizationIdClient = typeof client<
  DeactivateUsersByOrganizationIdMutationResponse,
  Error,
  DeactivateUsersByOrganizationIdMutationRequest
>
type DeactivateUsersByOrganizationId = {
  data: DeactivateUsersByOrganizationIdMutationResponse
  error: Error
  request: DeactivateUsersByOrganizationIdMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<DeactivateUsersByOrganizationIdClient>>
  client: {
    parameters: Partial<Parameters<DeactivateUsersByOrganizationIdClient>[0]>
    return: Awaited<ReturnType<DeactivateUsersByOrganizationIdClient>>
  }
}
/**
 * @summary Deactivate users by organization id
 * @link /v1/user/deactivate-users-by-organization-id
 */
export function useDeactivateUsersByOrganizationId(
  options: {
    mutation?: UseMutationOptions<
      DeactivateUsersByOrganizationId["response"],
      DeactivateUsersByOrganizationId["error"],
      DeactivateUsersByOrganizationId["request"]
    >
    client?: DeactivateUsersByOrganizationId["client"]["parameters"]
  } = {}
): UseMutationResult<
  DeactivateUsersByOrganizationId["response"],
  DeactivateUsersByOrganizationId["error"],
  DeactivateUsersByOrganizationId["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    DeactivateUsersByOrganizationId["response"],
    DeactivateUsersByOrganizationId["error"],
    DeactivateUsersByOrganizationId["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        DeactivateUsersByOrganizationId["data"],
        DeactivateUsersByOrganizationId["error"],
        DeactivateUsersByOrganizationId["request"]
      >({
        method: "put",
        url: `/v1/user/deactivate-users-by-organization-id`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
